import React, { useEffect, useMemo, useState } from 'react';
import { Container, Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { ThemedDropdownItem } from '../../components/themed/ThemedComponents';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const StatusPill = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    background: ${(p) =>
        p.$tone === 'ok'
            ? palette.pastel.aspargus
            : p.$tone === 'warn'
              ? palette.pastel.orange
              : palette.graphite.cloud};
    color: ${(p) =>
        p.$tone === 'ok'
            ? palette.evergreen.aspargus
            : p.$tone === 'warn'
              ? palette.vibrant.coral
              : palette.graphite.charcoal};
`;

const StepCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 10px;
`;

const StepTitle = styled(TextDefault)`
    display: block;
    font-weight: 700;
`;

const StepDetail = styled(TextTiny)`
    display: block;
    line-height: 1.45;
`;

const ValueRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 0;
    border-bottom: 1px solid ${palette.graphite.silver};
    &:last-child {
        border-bottom: none;
    }
`;

const ValuesBox = styled.div`
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    padding: 4px 14px;
    margin: 8px 0 14px;
    background: ${palette.graphite.cloud};
`;

const Select = styled.select`
    width: 100%;
    padding: 8px;
    font-size: 13px;
`;

const Input = styled.input`
    width: 100%;
    padding: 8px;
    font-size: 13px;
    box-sizing: border-box;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    th,
    td {
        text-align: left;
        padding: 10px 8px;
        border-bottom: 1px solid ${palette.graphite.silver};
        vertical-align: top;
    }
    th {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${palette.graphite.steel};
    }
    tr {
        cursor: pointer;
    }
    tr:hover td {
        background: ${palette.graphite.cloud};
    }
`;

const SectionIntro = styled(TextTiny)`
    display: block;
    margin-bottom: 12px;
    line-height: 1.45;
`;

function setupTone(status) {
    if (status === 'ready') return 'ok';
    if (status === 'action_required' || status === 'needs_setup') return 'warn';
    return 'neutral';
}

function setupLabel(status) {
    if (status === 'ready') return 'Ready';
    if (status === 'action_required') return 'Action required';
    if (status === 'needs_setup') return 'Needs setup';
    return status || '—';
}

const CREATE_NEW = '__create_new__';

function AwarePhishDelivery() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const {
        delivery,
        deliveryProfiles,
        domains,
        loadingDelivery,
        loadingDeliveryProfiles,
        savingAsset,
    } = awarePhishStore;

    const [selectedId, setSelectedId] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newSenderId, setNewSenderId] = useState('');
    const [newLandingId, setNewLandingId] = useState('');
    const [creatingSender, setCreatingSender] = useState(false);
    const [creatingLanding, setCreatingLanding] = useState(false);
    const [senderHost, setSenderHost] = useState('');
    const [senderMode, setSenderMode] = useState('mailgun');
    const [senderFrom, setSenderFrom] = useState('');
    const [senderSmtpHost, setSenderSmtpHost] = useState('');
    const [senderSmtpUser, setSenderSmtpUser] = useState('');
    const [senderSmtpPass, setSenderSmtpPass] = useState('');
    const [landingHost, setLandingHost] = useState('');
    const [lastCreatedDomain, setLastCreatedDomain] = useState(null);

    const [reportingMailbox, setReportingMailbox] = useState('');
    const [tenantId, setTenantId] = useState('');
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');

    const reload = async () => {
        if (!companyId) return;
        await Promise.all([
            awarePhishStore.loadDelivery(companyId),
            awarePhishStore.loadDeliveryProfiles(companyId),
            awarePhishStore.loadDomains(companyId),
        ]);
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    useEffect(() => {
        if (!delivery) return;
        setReportingMailbox(delivery.reportingMailbox || '');
        const g = delivery.reportingGraph || {};
        setTenantId(g.tenantId || '');
        setClientId(g.clientId || '');
        setClientSecret('');
    }, [delivery]);

    useEffect(() => {
        if (!selectedId && deliveryProfiles.length) {
            setSelectedId(deliveryProfiles[0].id);
        }
    }, [deliveryProfiles, selectedId]);

    const selected = useMemo(
        () => deliveryProfiles.find((p) => p.id === selectedId) || null,
        [deliveryProfiles, selectedId]
    );

    const senders = useMemo(() => {
        const ready = (delivery && delivery.availableSenders) || [];
        const fromDomains = (domains || []).filter(
            (d) => d.kind === 'sender' && d.readyForUse
        );
        const byId = new Map();
        [...ready, ...fromDomains].forEach((d) => byId.set(d.id, d));
        return [...byId.values()];
    }, [delivery, domains]);

    const landings = useMemo(() => {
        const ready = (delivery && delivery.availableLandings) || [];
        const fromDomains = (domains || []).filter(
            (d) => d.kind === 'landing' && d.readyForUse
        );
        const byId = new Map();
        [...ready, ...fromDomains].forEach((d) => byId.set(d.id, d));
        return [...byId.values()];
    }, [delivery, domains]);

    const instructions = selected && selected.instructions;
    const guideSteps =
        (instructions && instructions.steps) ||
        (selected && selected.nextSteps) ||
        [];
    const graph = (delivery && delivery.reportingGraph) || {};

    const markDone = async () => {
        try {
            await awarePhishStore.saveDelivery(companyId, {
                deliveryStatus: 'verified',
                markInstructed: true,
            });
            await awarePhishStore.loadDeliveryProfiles(companyId);
            commonStore.success('Marked as done');
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Could not mark as done'
            );
        }
    };

    const sendTest = async () => {
        if (!selected) return;
        try {
            const r = await awarePhishStore.sendDeliveryTest(companyId, {
                deliveryProfileId: selected.id,
            });
            commonStore.success(
                `Test sent from ${
                    (r && r.from) || 'sender domain'
                } to your admin inbox${r && r.to ? ` (${r.to})` : ''}. Check Inbox.`
            );
            await reload();
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Could not send test email'
            );
        }
    };

    const createSenderDomain = async () => {
        try {
            const d = await awarePhishStore.createDomain(companyId, {
                kind: 'sender',
                hostname: senderHost,
                sendMode: senderMode,
                fromAddress:
                    senderFrom ||
                    (senderHost ? `noreply@${senderHost}` : undefined),
                smtpHost: senderMode === 'smtp' ? senderSmtpHost : undefined,
                smtpUsername:
                    senderMode === 'smtp' ? senderSmtpUser : undefined,
                smtpPassword:
                    senderMode === 'smtp' ? senderSmtpPass : undefined,
            });
            setLastCreatedDomain(d);
            setCreatingSender(false);
            setSenderHost('');
            setSenderFrom('');
            setSenderSmtpHost('');
            setSenderSmtpUser('');
            setSenderSmtpPass('');
            setSenderMode('mailgun');
            if (d && d.readyForUse) {
                setNewSenderId(d.id);
                commonStore.success('Sender domain ready');
            } else {
                setNewSenderId('');
                commonStore.success(
                    'Sender domain created — finish DNS, then AwareGO will mark it ready'
                );
            }
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Could not create sender domain'
            );
        }
    };

    const createLandingDomain = async () => {
        try {
            const d = await awarePhishStore.createDomain(companyId, {
                kind: 'landing',
                hostname: landingHost,
            });
            setLastCreatedDomain(d);
            setCreatingLanding(false);
            setLandingHost('');
            setNewLandingId('');
            commonStore.success(
                'Landing domain created — add the CNAME, then AwareGO will mark it ready'
            );
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Could not create landing domain'
            );
        }
    };

    const createCustom = async () => {
        try {
            const p = await awarePhishStore.saveDeliveryProfile(
                companyId,
                null,
                {
                    name: newName,
                    senderDomainId: newSenderId,
                    landingDomainId: newLandingId,
                }
            );
            commonStore.success('Delivery profile created');
            setShowCreate(false);
            setNewName('');
            setNewSenderId('');
            setNewLandingId('');
            setCreatingSender(false);
            setCreatingLanding(false);
            if (p && p.id) setSelectedId(p.id);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to create profile'
            );
        }
    };

    const saveReporting = async () => {
        try {
            await awarePhishStore.saveReporting(companyId, {
                reportingMailbox,
                tenantId,
                clientId,
                clientSecret: clientSecret || undefined,
            });
            setClientSecret('');
            commonStore.success('Phishing Reporting settings saved');
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save reporting settings'
            );
        }
    };

    const testReporting = async () => {
        try {
            await awarePhishStore.saveReporting(companyId, {
                reportingMailbox,
                tenantId,
                clientId,
                clientSecret: clientSecret || undefined,
            });
            setClientSecret('');
            const r = await awarePhishStore.testReporting(companyId);
            commonStore.success(
                r && r.canReadInbox
                    ? 'Connection OK — mailbox is readable'
                    : 'Connection OK'
            );
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Reporting connection test failed'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH delivery</title>
            </Helmet>
            <HeaderSection
                heading="Delivery"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Before you run a simulation, allow AwarePHISH mail through your filters so messages land in Inbox—not quarantine. Pick a profile, follow the steps, send a test, then mark done.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
            />
            <AwarePhishSubNav active="delivery" />

            {(loadingDelivery || loadingDeliveryProfiles) &&
            !deliveryProfiles.length ? (
                <TextDefault lighter>Loading…</TextDefault>
            ) : (
                <>
                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <FlexDiv
                            alignCenter
                            justifyBetween
                            style={{ marginBottom: 8 }}
                        >
                            <div>
                                <Heading3>Profiles</Heading3>
                                <SectionIntro lighter>
                                    A profile pairs a sender domain with a
                                    landing page. Built-in profiles use matching
                                    AwareGO domains. Choose one when you create
                                    a simulation.
                                </SectionIntro>
                            </div>
                            <Dropdown
                                isOpen={addMenuOpen}
                                toggle={() => setAddMenuOpen((v) => !v)}
                            >
                                <DropdownToggle
                                    caret
                                    tag="button"
                                    type="button"
                                    className="btn"
                                    style={{
                                        background: palette.pastel.aspargus,
                                        color: palette.evergreen.aspargus,
                                        border: 'none',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        padding: '8px 14px',
                                    }}
                                >
                                    Add custom
                                </DropdownToggle>
                                <DropdownMenu end>
                                    <ThemedDropdownItem
                                        onClick={() => {
                                            setShowCreate(true);
                                            setAddMenuOpen(false);
                                        }}
                                    >
                                        Create custom profile…
                                    </ThemedDropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </FlexDiv>

                        {showCreate ? (
                            <div
                                style={{
                                    marginBottom: 16,
                                    padding: 14,
                                    border: `1px solid ${palette.graphite.silver}`,
                                    borderRadius: 6,
                                }}
                            >
                                <TextDefault bold style={{ display: 'block' }}>
                                    Create custom profile
                                </TextDefault>
                                <SectionIntro lighter>
                                    Pair a ready sender domain with a ready
                                    landing domain. Use Create new… to add your
                                    own domain (DNS instructions included).
                                </SectionIntro>
                                <FieldLabel>Name</FieldLabel>
                                <Input
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="e.g. Cross-domain pilot"
                                />
                                <FieldLabel>Sender domain</FieldLabel>
                                <Select
                                    value={
                                        creatingSender
                                            ? CREATE_NEW
                                            : newSenderId
                                    }
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === CREATE_NEW) {
                                            setCreatingSender(true);
                                            setNewSenderId('');
                                        } else {
                                            setCreatingSender(false);
                                            setNewSenderId(v);
                                        }
                                    }}
                                >
                                    <option value="">Select…</option>
                                    <option value={CREATE_NEW}>
                                        Create new…
                                    </option>
                                    {senders.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.hostname}
                                            {d.isBuiltin ? ' (AwareGO)' : ''}
                                        </option>
                                    ))}
                                </Select>
                                {creatingSender ? (
                                    <div
                                        style={{
                                            marginTop: 10,
                                            padding: 12,
                                            background: palette.graphite.cloud,
                                            borderRadius: 6,
                                        }}
                                    >
                                        <TextDefault
                                            bold
                                            style={{ display: 'block' }}
                                        >
                                            New sender domain
                                        </TextDefault>
                                        <SectionIntro lighter>
                                            Built-in AwareGO domains send via
                                            Mailgun. For your domain, choose
                                            Mailgun (DNS only) or your own SMTP.
                                        </SectionIntro>
                                        <FieldLabel>Hostname</FieldLabel>
                                        <Input
                                            value={senderHost}
                                            onChange={(e) =>
                                                setSenderHost(e.target.value)
                                            }
                                            placeholder="sim.yourcompany.com"
                                        />
                                        <FieldLabel>Send via</FieldLabel>
                                        <Select
                                            value={senderMode}
                                            onChange={(e) =>
                                                setSenderMode(e.target.value)
                                            }
                                        >
                                            <option value="mailgun">
                                                AwareGO Mailgun (DNS setup)
                                            </option>
                                            <option value="smtp">
                                                Our own SMTP
                                            </option>
                                        </Select>
                                        {senderMode === 'smtp' ? (
                                            <>
                                                <FieldLabel>
                                                    From address
                                                </FieldLabel>
                                                <Input
                                                    value={senderFrom}
                                                    onChange={(e) =>
                                                        setSenderFrom(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder={`noreply@${
                                                        senderHost ||
                                                        'your-domain.com'
                                                    }`}
                                                />
                                                <FieldLabel>
                                                    SMTP host (host:port)
                                                </FieldLabel>
                                                <Input
                                                    value={senderSmtpHost}
                                                    onChange={(e) =>
                                                        setSenderSmtpHost(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="smtp.example.com:587"
                                                />
                                                <FieldLabel>
                                                    SMTP username
                                                </FieldLabel>
                                                <Input
                                                    value={senderSmtpUser}
                                                    onChange={(e) =>
                                                        setSenderSmtpUser(
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                <FieldLabel>
                                                    SMTP password
                                                </FieldLabel>
                                                <Input
                                                    type="password"
                                                    value={senderSmtpPass}
                                                    onChange={(e) =>
                                                        setSenderSmtpPass(
                                                            e.target.value
                                                        )
                                                    }
                                                    autoComplete="new-password"
                                                />
                                            </>
                                        ) : (
                                            <SectionIntro
                                                lighter
                                                style={{ marginTop: 8 }}
                                            >
                                                After create: publish SPF, DKIM,
                                                and DMARC for this hostname.
                                                AwareGO must verify the domain
                                                on Mailgun before it becomes
                                                ready.
                                            </SectionIntro>
                                        )}
                                        <FlexDiv
                                            gap={12}
                                            style={{ marginTop: 12 }}
                                        >
                                            <ThemedButton
                                                text={
                                                    savingAsset
                                                        ? 'Saving…'
                                                        : 'Save sender domain'
                                                }
                                                mode="primary"
                                                disabled={
                                                    savingAsset ||
                                                    !senderHost.trim() ||
                                                    (senderMode === 'smtp' &&
                                                        (!senderSmtpHost.trim() ||
                                                            !senderSmtpPass))
                                                }
                                                onClick={createSenderDomain}
                                                $noWrap
                                            />
                                            <ThemedButton
                                                text="Cancel"
                                                mode="secondary"
                                                onClick={() =>
                                                    setCreatingSender(false)
                                                }
                                                $noWrap
                                            />
                                        </FlexDiv>
                                    </div>
                                ) : null}

                                <FieldLabel>Landing domain</FieldLabel>
                                <Select
                                    value={
                                        creatingLanding
                                            ? CREATE_NEW
                                            : newLandingId
                                    }
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === CREATE_NEW) {
                                            setCreatingLanding(true);
                                            setNewLandingId('');
                                        } else {
                                            setCreatingLanding(false);
                                            setNewLandingId(v);
                                        }
                                    }}
                                >
                                    <option value="">Select…</option>
                                    <option value={CREATE_NEW}>
                                        Create new…
                                    </option>
                                    {landings.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.hostname}
                                            {d.isBuiltin ? ' (AwareGO)' : ''}
                                        </option>
                                    ))}
                                </Select>
                                {creatingLanding ? (
                                    <div
                                        style={{
                                            marginTop: 10,
                                            padding: 12,
                                            background: palette.graphite.cloud,
                                            borderRadius: 6,
                                        }}
                                    >
                                        <TextDefault
                                            bold
                                            style={{ display: 'block' }}
                                        >
                                            New landing domain
                                        </TextDefault>
                                        <SectionIntro lighter>
                                            Add a CNAME (or A record) for your
                                            hostname to the AwarePHISH landing
                                            host AwareGO provides. Then contact
                                            support to mark it ready.
                                        </SectionIntro>
                                        <FieldLabel>Hostname</FieldLabel>
                                        <Input
                                            value={landingHost}
                                            onChange={(e) =>
                                                setLandingHost(e.target.value)
                                            }
                                            placeholder="links.yourcompany.com"
                                        />
                                        <FlexDiv
                                            gap={12}
                                            style={{ marginTop: 12 }}
                                        >
                                            <ThemedButton
                                                text={
                                                    savingAsset
                                                        ? 'Saving…'
                                                        : 'Save landing domain'
                                                }
                                                mode="primary"
                                                disabled={
                                                    savingAsset ||
                                                    !landingHost.trim()
                                                }
                                                onClick={createLandingDomain}
                                                $noWrap
                                            />
                                            <ThemedButton
                                                text="Cancel"
                                                mode="secondary"
                                                onClick={() =>
                                                    setCreatingLanding(false)
                                                }
                                                $noWrap
                                            />
                                        </FlexDiv>
                                    </div>
                                ) : null}

                                {lastCreatedDomain &&
                                lastCreatedDomain.customerInstructions ? (
                                    <div style={{ marginTop: 12 }}>
                                        <FieldLabel>
                                            Setup for{' '}
                                            {lastCreatedDomain.hostname}
                                        </FieldLabel>
                                        {(
                                            lastCreatedDomain
                                                .customerInstructions.steps ||
                                            []
                                        ).map((s) => (
                                            <StepCard key={s.id}>
                                                <StepTitle>{s.title}</StepTitle>
                                                <StepDetail lighter>
                                                    {s.detail}
                                                </StepDetail>
                                            </StepCard>
                                        ))}
                                    </div>
                                ) : null}

                                <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                    <ThemedButton
                                        text={
                                            savingAsset
                                                ? 'Saving…'
                                                : 'Create profile'
                                        }
                                        mode="primary"
                                        disabled={
                                            savingAsset ||
                                            !newName.trim() ||
                                            !newSenderId ||
                                            !newLandingId ||
                                            creatingSender ||
                                            creatingLanding
                                        }
                                        onClick={createCustom}
                                        $noWrap
                                    />
                                    <ThemedButton
                                        text="Cancel"
                                        mode="secondary"
                                        onClick={() => {
                                            setShowCreate(false);
                                            setCreatingSender(false);
                                            setCreatingLanding(false);
                                        }}
                                        $noWrap
                                    />
                                </FlexDiv>
                            </div>
                        ) : null}

                        {!deliveryProfiles.length ? (
                            <TextTiny lighter>
                                No delivery profiles yet. Built-in AwareGO
                                profiles appear once sender and landing domains
                                are ready.
                            </TextTiny>
                        ) : (
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Profile</th>
                                        <th>Sender</th>
                                        <th>Landing</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deliveryProfiles.map((p) => (
                                        <tr
                                            key={p.id}
                                            onClick={() => setSelectedId(p.id)}
                                            style={{
                                                background:
                                                    selectedId === p.id
                                                        ? palette.graphite.cloud
                                                        : undefined,
                                            }}
                                        >
                                            <td>
                                                <TextDefault bold>
                                                    {p.name}
                                                </TextDefault>
                                            </td>
                                            <td>
                                                {(p.senderDomain &&
                                                    p.senderDomain.hostname) ||
                                                    '—'}
                                            </td>
                                            <td>
                                                {(p.landingDomain &&
                                                    p.landingDomain.hostname) ||
                                                    '—'}
                                            </td>
                                            <td>
                                                {p.isBuiltin
                                                    ? 'Built-in'
                                                    : 'Custom'}
                                            </td>
                                            <td>
                                                <StatusPill
                                                    $tone={setupTone(
                                                        p.setupStatus
                                                    )}
                                                >
                                                    {setupLabel(p.setupStatus)}
                                                </StatusPill>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Block>

                    {selected ? (
                        <Block padding="20" column style={{ marginBottom: 14 }}>
                            <FlexDiv
                                alignCenter
                                justifyBetween
                                style={{ marginBottom: 4 }}
                            >
                                <Heading3>{selected.name}</Heading3>
                                <StatusPill
                                    $tone={setupTone(selected.setupStatus)}
                                >
                                    {setupLabel(selected.setupStatus)}
                                </StatusPill>
                            </FlexDiv>
                            <SectionIntro lighter>
                                Sender:{' '}
                                {(selected.senderDomain &&
                                    selected.senderDomain.hostname) ||
                                    '—'}
                                {' · '}
                                Landing: {selected.landingUrl || '—'}
                                {' · '}
                                {selected.isBuiltin ? 'Built-in' : 'Custom'}
                            </SectionIntro>

                            {(selected.blockers || []).length ? (
                                <div style={{ marginBottom: 8 }}>
                                    <FieldLabel>
                                        Waiting before you can finish
                                    </FieldLabel>
                                    {(selected.blockers || []).map((b) => (
                                        <StepCard key={b.id}>
                                            <StepTitle>{b.title}</StepTitle>
                                            <StepDetail lighter>
                                                {b.detail}
                                            </StepDetail>
                                        </StepCard>
                                    ))}
                                </div>
                            ) : null}

                            {instructions ? (
                                <>
                                    <Heading3>
                                        {instructions.title ||
                                            'Setup instructions'}
                                    </Heading3>
                                    {instructions.why ? (
                                        <SectionIntro lighter>
                                            {instructions.why}
                                        </SectionIntro>
                                    ) : (
                                        <SectionIntro lighter>
                                            Follow these steps so simulation
                                            mail from this profile reaches
                                            Inbox.
                                        </SectionIntro>
                                    )}

                                    <FieldLabel>
                                        Values to enter in your mail system
                                    </FieldLabel>
                                    <ValuesBox>
                                        <ValueRow>
                                            <TextTiny lighter>
                                                Sender domain (paste into
                                                Advanced delivery → Domain)
                                            </TextTiny>
                                            <TextDefault bold>
                                                {instructions.senderDomain ||
                                                    '—'}
                                            </TextDefault>
                                        </ValueRow>
                                        <ValueRow>
                                            <TextTiny lighter>
                                                Sending IP(s)
                                            </TextTiny>
                                            <TextDefault bold>
                                                {(
                                                    instructions.sendingIps ||
                                                    []
                                                ).join(', ') || '—'}
                                            </TextDefault>
                                        </ValueRow>
                                        <ValueRow>
                                            <TextTiny lighter>
                                                Landing page
                                            </TextTiny>
                                            <TextDefault bold>
                                                {instructions.landingUrl ||
                                                    '—'}
                                            </TextDefault>
                                        </ValueRow>
                                    </ValuesBox>
                                </>
                            ) : (
                                <SectionIntro lighter>
                                    Follow the steps below so simulation mail
                                    from this profile reaches Inbox.
                                </SectionIntro>
                            )}

                            {guideSteps.length ? (
                                <div>
                                    <FieldLabel>Steps</FieldLabel>
                                    {guideSteps.map((s, index) => (
                                        <StepCard key={s.id || index}>
                                            <StepTitle>
                                                {index + 1}. {s.title}
                                            </StepTitle>
                                            <StepDetail lighter>
                                                {s.detail}
                                            </StepDetail>
                                        </StepCard>
                                    ))}
                                </div>
                            ) : null}

                            <SectionIntro lighter>
                                Send test email uses this profile’s sender
                                domain (Mailgun for AwareGO builtins) and goes
                                to your signed-in admin inbox.
                            </SectionIntro>

                            <FlexDiv gap={12} style={{ marginTop: 16 }} $noWrap>
                                <ThemedButton
                                    text={
                                        savingAsset
                                            ? 'Sending…'
                                            : 'Send test email'
                                    }
                                    mode="primary"
                                    disabled={
                                        savingAsset ||
                                        !!(selected.blockers || []).length
                                    }
                                    onClick={sendTest}
                                    $noWrap
                                />
                                <ThemedButton
                                    text={
                                        savingAsset ? 'Saving…' : 'Mark Done'
                                    }
                                    mode="secondary"
                                    disabled={savingAsset}
                                    onClick={markDone}
                                    $noWrap
                                />
                                {!selected.isBuiltin ? (
                                    <ThemedButton
                                        text="Delete custom profile"
                                        mode="secondary"
                                        disabled={savingAsset}
                                        onClick={async () => {
                                            try {
                                                await awarePhishStore.removeDeliveryProfile(
                                                    companyId,
                                                    selected.id
                                                );
                                                setSelectedId(null);
                                                commonStore.success(
                                                    'Profile deleted'
                                                );
                                            } catch (e) {
                                                commonStore.error(
                                                    awarePhishStore.error ||
                                                        'Delete failed'
                                                );
                                            }
                                        }}
                                        $noWrap
                                    />
                                ) : null}
                            </FlexDiv>
                        </Block>
                    ) : null}

                    <Block padding="20" column>
                        <Heading3>Phishing Reporting</Heading3>
                        <SectionIntro lighter>
                            So when an employee taps Report in Outlook,
                            AwarePHISH can mark matching simulation recipients
                            as Reported. Microsoft 365 only. Reports must land
                            in a shared mailbox in your tenant; AwarePHISH then
                            reads that mailbox.
                        </SectionIntro>

                        <FieldLabel>How to set this up</FieldLabel>
                        <StepCard>
                            <StepTitle>
                                1. Create a shared reporting mailbox
                            </StepTitle>
                            <StepDetail lighter>
                                In Microsoft 365, create a shared mailbox (for
                                example phishing-reports@yourcompany.com). In
                                Defender → Settings → Email & collaboration →
                                User reported settings, send reported messages
                                to that mailbox.
                            </StepDetail>
                        </StepCard>
                        <StepCard>
                            <StepTitle>
                                2. Register an app in Microsoft Entra
                            </StepTitle>
                            <StepDetail lighter>
                                Entra admin center → App registrations → New
                                registration. Under API permissions add
                                Application permission Microsoft Graph →
                                Mail.Read, then Grant admin consent. Under
                                Certificates & secrets create a client secret.
                                Copy Directory (tenant) ID, Application (client)
                                ID, and the secret value into the fields below.
                            </StepDetail>
                        </StepCard>
                        <StepCard>
                            <StepTitle>3. Save and Test</StepTitle>
                            <StepDetail lighter>
                                Enter the mailbox and app details, click Test to
                                confirm AwarePHISH can read the Inbox, then use
                                Sync reports after employees start reporting.
                            </StepDetail>
                        </StepCard>

                        <FieldLabel>Reporting mailbox</FieldLabel>
                        <Input
                            value={reportingMailbox}
                            onChange={(e) =>
                                setReportingMailbox(e.target.value)
                            }
                            placeholder="phishing-reports@yourcompany.com"
                        />

                        <FieldLabel>Directory (tenant) ID</FieldLabel>
                        <Input
                            value={tenantId}
                            onChange={(e) => setTenantId(e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            autoComplete="off"
                        />

                        <FieldLabel>Application (client) ID</FieldLabel>
                        <Input
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            autoComplete="off"
                        />

                        <FieldLabel>Client secret</FieldLabel>
                        <Input
                            type="password"
                            value={clientSecret}
                            onChange={(e) => setClientSecret(e.target.value)}
                            placeholder={
                                graph.clientSecretConfigured
                                    ? '•••••••• (leave blank to keep current)'
                                    : 'Paste the secret value from Entra'
                            }
                            autoComplete="new-password"
                        />

                        <FlexDiv gap={12} style={{ marginTop: 16 }} $noWrap>
                            <ThemedButton
                                text={savingAsset ? 'Saving…' : 'Save'}
                                mode="primary"
                                disabled={savingAsset}
                                onClick={saveReporting}
                                $noWrap
                            />
                            <ThemedButton
                                text={savingAsset ? 'Testing…' : 'Test'}
                                mode="secondary"
                                disabled={
                                    savingAsset || !reportingMailbox.trim()
                                }
                                onClick={testReporting}
                                $noWrap
                            />
                            <ThemedButton
                                text={
                                    savingAsset ? 'Syncing…' : 'Sync reports'
                                }
                                mode="secondary"
                                disabled={
                                    savingAsset ||
                                    !reportingMailbox.trim() ||
                                    !graph.credentialsConfigured
                                }
                                onClick={async () => {
                                    try {
                                        const r =
                                            await awarePhishStore.syncReports(
                                                companyId
                                            );
                                        commonStore.success(
                                            `Scanned ${(r && r.scanned) || 0}, reported ${(r && r.reported) || 0}, skipped ${(r && r.skipped) || 0}`
                                        );
                                    } catch (e) {
                                        commonStore.error(
                                            awarePhishStore.error ||
                                                'Report sync failed'
                                        );
                                    }
                                }}
                                $noWrap
                            />
                        </FlexDiv>
                    </Block>
                </>
            )}
        </Container>
    );
}

export default observer(AwarePhishDelivery);
